class AiRecommendations extends HTMLElement {
  constructor() {
    super();
    this.container = this.querySelector('.ai-recommendations-container');
    this.loading = this.querySelector('.ai-recommendations-loading');
    this.handle = this.dataset.handle;
    this.tenant = this.dataset.tenant || window.shopkeeperTenant || window.shopkeeper_tenant || 'rugtown';

    // Configurable base URL from Liquid dataset
    this.baseUrl = this.dataset.apiUrl || 'https://api.productadvisorai.com';
  }

  connectedCallback() {
    if (!this.handle) {
      this.handle = this.dataset.handle;
      this.tenant = this.dataset.tenant || window.shopkeeperTenant || window.shopkeeper_tenant || this.tenant || 'rugtown';
      this.baseUrl = this.dataset.apiUrl || this.baseUrl || 'https://api.productadvisorai.com';
      this.container = this.querySelector('.ai-recommendations-container');
      this.loading = this.querySelector('.ai-recommendations-loading');
    }
    if (!this.handle) return;

    // Fetch immediately on page load
    this.fetchRecommendations();

    // Listen for Shopify variant URL changes
    this.setupVariantListener();
  }

  disconnectedCallback() {
    window.removeEventListener('ai-variant-change', this.onVariantChange);
  }

  setupVariantListener() {
    this.onVariantChange = () => {
      if (this.hasLoaded) {
        if (this.container) this.container.innerHTML = '';
        if (this.loading) this.loading.style.display = 'block';
        this.fetchRecommendations();
      }
    };
    window.addEventListener('ai-variant-change', this.onVariantChange);

    if (!window._aiHistoryIntercepted) {
      const triggerEvent = () => window.dispatchEvent(new Event('ai-variant-change'));
      const originalPushState = history.pushState;
      history.pushState = function () {
        originalPushState.apply(this, arguments);
        triggerEvent();
      };
      const originalReplaceState = history.replaceState;
      history.replaceState = function () {
        originalReplaceState.apply(this, arguments);
        triggerEvent();
      };
      window.addEventListener('popstate', triggerEvent);
      window._aiHistoryIntercepted = true;
    }
  }

  async fetchRecommendations() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const shopifyVariantId = urlParams.get('variant') || this.dataset.initialVariant;

      let targetSku = this.dataset.initialVariantSku;

      if (shopifyVariantId && shopifyVariantId !== this.dataset.initialVariant) {
        try {
          const productRes = await fetch(`${window.location.pathname}.js`);
          if (productRes.ok) {
            const productJson = await productRes.json();
            const matchingVariant = productJson.variants.find(v => v.id.toString() === shopifyVariantId.toString());
            if (matchingVariant && matchingVariant.sku) {
              targetSku = matchingVariant.sku;
            }
          }
        } catch (e) {
          console.warn('[AI Recommendations] Failed to fetch variant SKU', e);
        }
      }

      const activeTenant = this.dataset.tenant || window.shopkeeperTenant || window.shopkeeper_tenant || this.tenant || 'rugtown';
      let url = `${this.baseUrl}/api/recommendations/pdp?tenant_id=${encodeURIComponent(activeTenant)}&product_handle=${encodeURIComponent(this.handle)}&limit_per_strategy=8`;

      if (targetSku) {
        url += `&variant_id=${encodeURIComponent(targetSku)}`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`API returned status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.sections || data.sections.length === 0) {
        this.showEmptyState();
        return;
      }

      this.render(data.sections);
      this.hasLoaded = true;

      // Fire impression event
      this.trackEvent('impression', null, null);

    } catch (error) {
      console.error('[AI Recommendations] Failed to fetch:', error);
      this.showEmptyState();
    }
  }

  showEmptyState() {
    if (this.loading) this.loading.style.display = 'none';
    this.style.display = 'none';
  }

  formatMoney(cents) {
    if (cents === null || cents === undefined || cents === '') return '';
    if (typeof cents === 'string' && cents.trim().startsWith('$')) return cents.trim();
    const num = Number(cents);
    if (isNaN(num)) return String(cents);
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  }

  render(sections) {
    if (this.loading) this.loading.style.display = 'none';
    this.container.innerHTML = '';

    const sectionTemplate = document.getElementById('ai-section-template');
    const cardTemplate = document.getElementById('ai-card-template');

    if (!sectionTemplate || !cardTemplate) return;

    sections.forEach(sectionData => {
      if (!sectionData.recommendations || sectionData.recommendations.length === 0) return;

      const sectionNode = sectionTemplate.content.cloneNode(true);
      const titleEl = sectionNode.querySelector('.ai-section-title');
      const gridEl = sectionNode.querySelector('.ai-section-grid');

      if (titleEl) titleEl.textContent = sectionData.title;

      sectionData.recommendations.forEach(rec => {
        const cardNode = cardTemplate.content.cloneNode(true);
        const shopifyHandle = rec.handle || rec.product_handle || (rec.url ? rec.url.split('/').pop().split('?')[0] : '');
        const baseProductUrl = `/products/${shopifyHandle}`;

        // Populate Image
        const img = cardNode.querySelector('img');
        if (img && rec.image_url) {
          const originalSrc = rec.image_url;
          let webpSrc = originalSrc;
          try {
            const urlParts = originalSrc.split('/');
            const filename = urlParts.pop();
            const newFilename = filename.replace(/\.(jpg|jpeg|png)$/i, '.webp');
            webpSrc = urlParts.join('/') + '/card/' + newFilename;
          } catch (e) {
            webpSrc = originalSrc.replace(/\.(jpg|jpeg|png)$/i, '.webp');
          }

          if (window.CloudImageHandler && window.CloudImageHandler.optimizeImageUrl) {
            img.src = window.CloudImageHandler.optimizeImageUrl(webpSrc);
            img.setAttribute('data-current-url', webpSrc);
          } else {
            img.src = webpSrc;
          }
          img.srcset = '';
          img.alt = rec.title || '';
          img.onerror = function () {
            if (this.src.endsWith('.webp')) {
              this.src = originalSrc;
              return;
            }
            if (window.CloudImageHandler) {
              const fakeImagesArray = btoa(JSON.stringify([{ url: originalSrc }]));
              window.CloudImageHandler.onError(this, fakeImagesArray, shopifyHandle, rec.title);
            } else {
              this.src = 'https://cdn.shopify.com/s/images/themes/product-1.png';
            }
          };

          // "See in My Room" overlay button
          const seeInRoomBtn = cardNode.querySelector('.see-in-room-overlay-btn, .see-in-room-btn');
          if (seeInRoomBtn) {
            if (!this.isRugProduct(rec)) {
              seeInRoomBtn.remove();
            } else {
              const visualizerRugUrl = rec.visualizer_image_url || rec.gcs_image_url || originalSrc || '';
              const cleanRug = typeof window.normalizeRugUrl === 'function' ? window.normalizeRugUrl(visualizerRugUrl) : visualizerRugUrl;
              seeInRoomBtn.setAttribute('data-product-handle', shopifyHandle);
              seeInRoomBtn.setAttribute('data-product-id', rec.id || rec.product_id || '');
              seeInRoomBtn.setAttribute('data-rug', cleanRug);
              seeInRoomBtn.setAttribute('data-product-url', baseProductUrl);
              seeInRoomBtn.setAttribute('data-shape', (rec.shape || '').toString().toLowerCase());
              seeInRoomBtn.setAttribute('data-size', rec.selected_size || rec.size_bucket || rec.size || '');
            }
          }
        }

        // Setup favorites button
        const favoritesBtn = cardNode.querySelector('favorites-button');
        if (favoritesBtn) {
          favoritesBtn.setAttribute('product-handle', shopifyHandle);
          favoritesBtn.setAttribute('product-title', rec.title || '');
          favoritesBtn.setAttribute('product-image', rec.primary_image_url || rec.image_url || rec.gcs_image_url || '');
          favoritesBtn.setAttribute('product-price', this.formatMoney(rec.price));
          favoritesBtn.setAttribute('product-url', baseProductUrl);
        }

        // Populate Sale Badge & Pricing
        const saleBadge = cardNode.querySelector('.ai-sale-badge');
        const regPriceEl = cardNode.querySelector('.price__regular');
        const salePriceEl = cardNode.querySelector('.price__sale');

        if (rec.compare_at_price && Number(rec.compare_at_price) > Number(rec.price)) {
          if (regPriceEl) regPriceEl.style.display = 'none';
          if (salePriceEl) salePriceEl.style.display = 'flex';

          const salePriceItem = cardNode.querySelector('.ai-sale-price, .price__sale .ai-product-price, .price-item--sale');
          const comparePriceItem = cardNode.querySelector('.ai-product-compare-price');
          if (salePriceItem) salePriceItem.textContent = this.formatMoney(rec.price);
          if (comparePriceItem) comparePriceItem.textContent = this.formatMoney(rec.compare_at_price);

          if (saleBadge) {
            const percentOff = Math.round(((Number(rec.compare_at_price) - Number(rec.price)) / Number(rec.compare_at_price)) * 100);
            saleBadge.textContent = `SAVE ${percentOff}%`;
            saleBadge.style.display = 'inline-block';
          }
        } else {
          if (regPriceEl) regPriceEl.style.display = 'flex';
          if (salePriceEl) salePriceEl.style.display = 'none';
          if (saleBadge) saleBadge.style.display = 'none';

          const regPriceItem = cardNode.querySelector('.ai-regular-price, .price__regular .ai-product-price, .price-item--regular');
          if (regPriceItem) regPriceItem.textContent = this.formatMoney(rec.price);
        }

        // Low-stock "Only a few left!" Badge
        const stockBadge = cardNode.querySelector('.ai-stock-badge');
        if (stockBadge) {
          if (rec.inventory_status === 'low_stock' || rec.is_low_stock || (rec.inventory_quantity && rec.inventory_quantity <= 5 && rec.inventory_quantity > 0) || rec.low_stock) {
            stockBadge.style.display = 'inline-block';
            if (rec.stock_message) {
              stockBadge.textContent = rec.stock_message;
            }
          } else {
            stockBadge.style.display = 'none';
          }
        }

        // Populate Vendor
        const vendorEl = cardNode.querySelector('.ai-product-vendor');
        if (vendorEl) {
          const vendorName = rec.vendor || rec.brand || rec.vendor_name || '';
          if (vendorName) {
            vendorEl.textContent = vendorName;
            vendorEl.style.display = 'block';
          }
        }

        // Populate Title
        const titleEl = cardNode.querySelector('.ai-product-title');
        if (titleEl) {
          titleEl.textContent = rec.title;
        }

        // Populate Size Swatches
        const swatchContainer = cardNode.querySelector('.ai-product-swatches-container');
        const sizeSwatchEl = cardNode.querySelector('.ai-product-size-swatch');
        if (swatchContainer && sizeSwatchEl && (rec.size || rec.selected_size)) {
          sizeSwatchEl.textContent = rec.selected_size || rec.size;
          swatchContainer.style.display = 'block';
        }

        // Populate Links
        const linkEls = cardNode.querySelectorAll('a.ai-product-link');
        let primaryLink = null;
        linkEls.forEach(link => {
          link.href = baseProductUrl;
          if (!primaryLink) primaryLink = link;
        });

        // Click on entire card to navigate
        const cardItem = cardNode.querySelector('.ai-carousel-item, .ai-card-inner');
        if (cardItem) {
          cardItem.style.cursor = 'pointer';
          cardItem.addEventListener('click', (e) => {
            if (isDraggingGlobal) return;
            if (e.target.closest('button, input, select, favorites-button, .see-in-room-overlay-btn, .ai-add-to-cart-btn, .ai-add-to-cart-wrapper, .ai-wishlist-btn')) {
              return;
            }
            if (baseProductUrl && baseProductUrl !== '#' && !baseProductUrl.endsWith('#')) {
              window.location.href = baseProductUrl;
            }
          });
        }

        // Asynchronously resolve the numeric variant ID & Vendor from Shopify
        let numericVariantId = null;
        if (shopifyHandle) {
          fetch(`/products/${shopifyHandle}.js`)
            .then(res => res.json())
            .then(prodData => {
              if (prodData) {
                if (vendorEl && !vendorEl.textContent && prodData.vendor) {
                  vendorEl.textContent = prodData.vendor;
                  vendorEl.style.display = 'block';
                }

                if (prodData.variants && prodData.variants.length > 0) {
                  let matchedVariant = null;
                  if (rec.variant_id) {
                    matchedVariant = prodData.variants.find(v =>
                      v.sku === rec.variant_id ||
                      (v.id && String(v.id) === String(rec.variant_id))
                    );
                  }
                  if (!matchedVariant) {
                    matchedVariant = prodData.variants[0];
                  }

                  if (matchedVariant && matchedVariant.id) {
                    numericVariantId = matchedVariant.id;
                    const targetUrl = `/products/${shopifyHandle}?variant=${matchedVariant.id}`;
                    linkEls.forEach(link => {
                      link.href = targetUrl;
                    });

                    const addToCartBtn = cardNode.querySelector('.ai-add-to-cart-btn');
                    const addToCartText = cardNode.querySelector('.ai-add-to-cart-text');
                    if (addToCartBtn) {
                      if (matchedVariant.available) {
                        addToCartBtn.disabled = false;
                        if (addToCartText) addToCartText.textContent = 'Add to cart';
                      } else {
                        addToCartBtn.disabled = true;
                        if (addToCartText) addToCartText.textContent = 'Sold Out';
                      }
                    }
                  }
                }
              }
            })
            .catch(() => {
              const addToCartBtn = cardNode.querySelector('.ai-add-to-cart-btn');
              if (addToCartBtn) addToCartBtn.disabled = false;
            });
        }

        // Attach click tracking
        if (primaryLink) {
          primaryLink.addEventListener('click', () => {
            this.trackEvent('click', shopifyHandle, rec.rank, rec.rec_type);
          });
        }

        // Add to Cart AJAX Execution Logic (No Page Redirection)
        const addToCartBtn = cardNode.querySelector('.ai-add-to-cart-btn');
        const addToCartText = cardNode.querySelector('.ai-add-to-cart-text');
        if (addToCartBtn) {
          addToCartBtn.addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopPropagation();

            let targetId = numericVariantId || rec.variant_id;
            if (targetId && typeof targetId === 'string' && targetId.includes('/')) {
              targetId = targetId.split('/').pop();
            }

            if (!targetId || isNaN(Number(targetId))) {
              try {
                const res = await fetch(`/products/${shopifyHandle}.js`);
                const prodData = await res.json();
                if (prodData && prodData.variants && prodData.variants.length > 0) {
                  const matched = prodData.variants.find(v => v.sku === rec.variant_id || String(v.id) === String(rec.variant_id));
                  targetId = matched ? matched.id : prodData.variants[0].id;
                  numericVariantId = targetId;
                }
              } catch (err) {}
            }

            const originalLabel = addToCartText ? addToCartText.textContent : 'Add to cart';

            if (!targetId || isNaN(Number(targetId))) {
              if (addToCartText) addToCartText.textContent = 'Unavailable';
              addToCartBtn.style.setProperty('background', '#777777', 'important');
              setTimeout(() => {
                if (addToCartText) addToCartText.textContent = originalLabel;
                addToCartBtn.style.removeProperty('background');
              }, 2000);
              return;
            }

            if (addToCartText) addToCartText.textContent = 'Adding...';
            addToCartBtn.disabled = true;

            try {
              const res = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  items: [{ id: Number(targetId), quantity: 1 }],
                  sections: 'cart-drawer,cart-icon-bubble,cart-notification'
                })
              });

              if (res.ok) {
                const cartData = await res.json();
                if (addToCartText) addToCartText.textContent = '✓ Added!';
                addToCartBtn.style.setProperty('background', '#2e7d32', 'important'); // success green

                // Fetch latest full cart state
                let fullCart = null;
                try {
                  const cartRes = await fetch('/cart.js');
                  if (cartRes.ok) fullCart = await cartRes.json();
                } catch (e) {}

                const effectiveCart = fullCart || cartData;

                // Update Header Cart Icon Badge without navigation
                if (cartData.sections && cartData.sections['cart-icon-bubble']) {
                  try {
                    const parsedBubble = new DOMParser().parseFromString(cartData.sections['cart-icon-bubble'], 'text/html');
                    const newBubble = parsedBubble.querySelector('#cart-icon-bubble');
                    const targetBubble = document.querySelector('#cart-icon-bubble');
                    if (newBubble && targetBubble) targetBubble.innerHTML = newBubble.innerHTML;
                  } catch (e) {}
                } else {
                  const bubbleCounts = document.querySelectorAll('.cart-count-bubble span, .header-actions__cart-badge, .cart-count, [data-cart-count]');
                  bubbleCounts.forEach(el => {
                    el.textContent = effectiveCart.item_count || '1';
                  });
                }

                // Dispatch native Shopify theme events
                document.dispatchEvent(new CustomEvent('cart:update', {
                  bubbles: true,
                  detail: {
                    resource: effectiveCart,
                    sourceId: 'ai-recommendations',
                    data: {
                      source: 'ai-recommendations',
                      variantId: String(targetId),
                      itemCount: effectiveCart.item_count
                    }
                  }
                }));
                document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: effectiveCart } }));
                document.dispatchEvent(new CustomEvent('cart:refresh', { detail: effectiveCart }));
                document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: effectiveCart } }));

                // Open native cart drawer or notification (without page redirect)
                const cartDrawer = document.querySelector('cart-drawer, cart-drawer-component, #CartDrawer, #cart-drawer');
                if (cartDrawer) {
                  if (cartData.sections && cartData.sections['cart-drawer'] && typeof cartDrawer.renderContents === 'function') {
                    try {
                      cartDrawer.renderContents(cartData);
                    } catch (e) {}
                  }
                  if (typeof cartDrawer.open === 'function') {
                    cartDrawer.open();
                  } else if (typeof cartDrawer.showDialog === 'function') {
                    cartDrawer.showDialog();
                  } else if (cartDrawer.classList && (cartDrawer.classList.contains('drawer') || cartDrawer.id === 'CartDrawer')) {
                    cartDrawer.classList.add('animate', 'active');
                    document.body.classList.add('overflow-hidden');
                  }
                } else {
                  const cartNotification = document.querySelector('cart-notification');
                  if (cartNotification && typeof cartNotification.open === 'function') {
                    if (cartData.sections && typeof cartNotification.renderContents === 'function') {
                      try { cartNotification.renderContents(cartData); } catch (e) { cartNotification.open(); }
                    } else {
                      cartNotification.open();
                    }
                  }
                }

                setTimeout(() => {
                  if (addToCartText) addToCartText.textContent = originalLabel;
                  addToCartBtn.style.removeProperty('background');
                  addToCartBtn.disabled = false;
                }, 2200);
              } else {
                if (addToCartText) addToCartText.textContent = 'Unavailable';
                addToCartBtn.style.setProperty('background', '#777777', 'important');
                setTimeout(() => {
                  if (addToCartText) addToCartText.textContent = originalLabel;
                  addToCartBtn.style.removeProperty('background');
                  addToCartBtn.disabled = false;
                }, 2000);
              }
            } catch (err) {
              if (addToCartText) addToCartText.textContent = 'Error';
              addToCartBtn.style.setProperty('background', '#777777', 'important');
              setTimeout(() => {
                if (addToCartText) addToCartText.textContent = originalLabel;
                addToCartBtn.style.removeProperty('background');
                addToCartBtn.disabled = false;
              }, 2000);
            }
          });
        }

        if (gridEl) gridEl.appendChild(cardNode);
      });

      const scroller = sectionNode.querySelector('.ai-section-grid');
      const prevBtn = sectionNode.querySelector('.ai-nav-prev, .slideshow-control--previous');
      const nextBtn = sectionNode.querySelector('.ai-nav-next, .slideshow-control--next');

      this.container.appendChild(sectionNode);

      if (scroller && prevBtn && nextBtn) {
        prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          scroller.scrollBy({ left: -(scroller.clientWidth * 0.8), behavior: 'smooth' });
        });
        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          scroller.scrollBy({ left: scroller.clientWidth * 0.8, behavior: 'smooth' });
        });
      }

      // High-performance Touch Swipe & Mouse Drag Controller
      if (scroller) {
        let isDown = false;
        let startX = 0;
        let startY = 0;
        let scrollLeft = 0;
        let isDragging = false;
        let isHorizontalGesture = null;
        let lastX = 0;
        let lastTime = 0;
        let velocity = 0;
        let momentumId = null;

        const stopMomentum = () => {
          if (momentumId) {
            cancelAnimationFrame(momentumId);
            momentumId = null;
          }
        };

        // --- Touch Swipe Listeners (Mobile & Tablet) ---
        scroller.addEventListener('touchstart', (e) => {
          if (e.touches.length !== 1) return;
          stopMomentum();
          const touch = e.touches[0];
          startX = touch.clientX;
          startY = touch.clientY;
          lastX = touch.clientX;
          scrollLeft = scroller.scrollLeft;
          lastTime = performance.now();
          velocity = 0;
          isDown = true;
          isDragging = false;
          isHorizontalGesture = null;
        }, { passive: true });

        scroller.addEventListener('touchmove', (e) => {
          if (!isDown || e.touches.length !== 1) return;
          const touch = e.touches[0];
          const dx = touch.clientX - startX;
          const dy = touch.clientY - startY;
          const now = performance.now();
          const dt = Math.max(1, now - lastTime);

          velocity = ((touch.clientX - lastX) / dt) * 16.6;
          lastX = touch.clientX;
          lastTime = now;

          if (isHorizontalGesture === null) {
            if (Math.abs(dx) > 6 || Math.abs(dy) > 6) {
              isHorizontalGesture = Math.abs(dx) > Math.abs(dy);
              if (isHorizontalGesture) {
                isDragging = true;
                isDraggingGlobal = true;
                scroller.style.scrollSnapType = 'none';
                scroller.style.scrollBehavior = 'auto';
              }
            }
          }

          if (isHorizontalGesture) {
            scroller.scrollLeft = scrollLeft - dx;
          }
        }, { passive: true });

        scroller.addEventListener('touchend', () => {
          if (!isDown) return;
          isDown = false;

          if (isHorizontalGesture) {
            if (Math.abs(velocity) > 1.2) {
              let currentVelocity = velocity * 1.5;
              const decay = 0.92;
              const step = () => {
                if (Math.abs(currentVelocity) > 0.5) {
                  scroller.scrollLeft -= currentVelocity;
                  currentVelocity *= decay;
                  momentumId = requestAnimationFrame(step);
                } else {
                  scroller.style.scrollSnapType = '';
                  scroller.style.scrollBehavior = '';
                }
              };
              momentumId = requestAnimationFrame(step);
            } else {
              scroller.style.scrollSnapType = '';
              scroller.style.scrollBehavior = '';
            }

            setTimeout(() => {
              isDragging = false;
              isDraggingGlobal = false;
              isHorizontalGesture = null;
            }, 80);
          } else {
            scroller.style.scrollSnapType = '';
            scroller.style.scrollBehavior = '';
            isDragging = false;
            isDraggingGlobal = false;
            isHorizontalGesture = null;
          }
        }, { passive: true });

        // --- Mouse Drag Listeners (Desktop) ---
        scroller.addEventListener('mousedown', (e) => {
          if (e.button !== 0) return;
          if (e.target.closest('button, input, select, favorites-button, .see-in-room-overlay-btn, .ai-add-to-cart-wrapper, .ai-add-to-cart-btn, .ai-wishlist-btn')) return;

          stopMomentum();
          isDown = true;
          isDragging = false;
          isDraggingGlobal = false;
          startX = e.clientX;
          lastX = e.clientX;
          scrollLeft = scroller.scrollLeft;
          lastTime = performance.now();
          velocity = 0;
        });

        window.addEventListener('mousemove', (e) => {
          if (!isDown) return;
          const currentX = e.clientX;
          const deltaX = currentX - startX;
          const now = performance.now();
          const dt = Math.max(1, now - lastTime);

          velocity = ((currentX - lastX) / dt) * 16.6;
          lastX = currentX;
          lastTime = now;

          if (Math.abs(deltaX) > 8) {
            if (!isDragging) {
              isDragging = true;
              isDraggingGlobal = true;
              scroller.style.scrollSnapType = 'none';
              scroller.style.scrollBehavior = 'auto';
              scroller.style.cursor = 'grabbing';
            }
            scroller.scrollLeft = scrollLeft - deltaX;
          }
        });

        window.addEventListener('mouseup', () => {
          if (!isDown) return;
          isDown = false;
          scroller.style.cursor = '';

          if (isDragging) {
            if (Math.abs(velocity) > 1.2) {
              let currentVelocity = velocity * 1.6;
              const decay = 0.93;
              const step = () => {
                if (Math.abs(currentVelocity) > 0.6) {
                  scroller.scrollLeft -= currentVelocity;
                  currentVelocity *= decay;
                  momentumId = requestAnimationFrame(step);
                } else {
                  scroller.style.scrollSnapType = '';
                  scroller.style.scrollBehavior = '';
                }
              };
              momentumId = requestAnimationFrame(step);
            } else {
              scroller.style.scrollSnapType = '';
              scroller.style.scrollBehavior = '';
            }

            setTimeout(() => {
              isDragging = false;
              isDraggingGlobal = false;
            }, 80);
          } else {
            scroller.style.scrollSnapType = '';
            scroller.style.scrollBehavior = '';
          }
        });

        scroller.addEventListener('click', (e) => {
          if (isDragging) {
            e.preventDefault();
            e.stopPropagation();
          }
        }, true);
      }
    });
  }

  trackEvent(eventType, candidateHandle, rank, recType) {
    if (!navigator.sendBeacon) return;

    const url = `${this.baseUrl}/api/recommendations/events`;

    const visitorId = window.AnalyticsContext ? window.AnalyticsContext.getVisitorId() : (this.getCookie('_shopify_y') || 'anonymous');
    const sessionId = window.AnalyticsContext ? window.AnalyticsContext.getSessionId() : null;
    const requestId = window.AnalyticsContext ? window.AnalyticsContext.generateRequestId() : null;

    const payload = {
      event_type: eventType === 'impression' ? 'recommendation_impression' : 'recommendation_click',
      tenant_id: this.tenant,
      source_handle: this.handle,
      visitor_id: visitorId,
      session_id: sessionId,
      request_id: requestId
    };

    if (candidateHandle) {
      payload.candidate_handle = candidateHandle;
      payload.rank = rank;
      if (recType) {
        payload.rec_type = recType;
      }
    }

    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    navigator.sendBeacon(url, blob);
  }

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  isRugProduct(rec) {
    if (typeof window.isRugProduct === 'function') {
      return window.isRugProduct(rec);
    }
    if (!rec) return false;
    const t = String(rec.title || rec.name || '').toLowerCase();
    const pt = String(rec.product_type || rec.type || rec.category || (Array.isArray(rec.categories) ? rec.categories.join(' ') : '') || '').toLowerCase();
    const d = String(rec.description || '').toLowerCase();
    const tg = Array.isArray(rec.tags) ? rec.tags.join(' ').toLowerCase() : String(rec.tags || '').toLowerCase();

    const isRug = t.includes('rug') || pt.includes('rug') || tg.includes('rug') || d.includes('rug') ||
                  t.includes('carpet') || pt.includes('carpet') || tg.includes('carpet') || d.includes('carpet') ||
                  t.includes('runner') || pt.includes('runner') || tg.includes('runner') || d.includes('runner');

    const isExempt = t.includes('pad') || pt.includes('pad') || tg.includes('pad') ||
                    t.includes('underlay') || pt.includes('underlay') || tg.includes('underlay') ||
                    t.includes('cleaner') || pt.includes('cleaner') || tg.includes('cleaner') ||
                    pt.includes('furniture') || tg.includes('furniture') ||
                    pt.includes('accessory') || tg.includes('accessory');

    return isRug && !isExempt;
  }
}

let isDraggingGlobal = false;

if (!customElements.get('ai-recommendations')) {
  customElements.define('ai-recommendations', AiRecommendations);
}
